import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, Upload, Trash } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UNITS, isAreaUnit } from '@billflow/shared';
import type { InventoryItem, Supplier, Unit } from '@billflow/shared';

interface SuppliersResponse {
  data: { suppliers: Supplier[] };
}

interface ItemEditModalProps {
  item: InventoryItem;
  onClose: () => void;
  onSaved: () => void;
}

const SELECT_CLS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export function ItemEditModal({ item, onClose, onSaved }: ItemEditModalProps) {
  const [code, setCode] = useState(item.code ?? '');
  const [name, setName] = useState(item.name);
  const [unit, setUnit] = useState<Unit>((item.unit as Unit) ?? 'Pcs');
  const [sellingDollars, setSellingDollars] = useState(String(item.selling_price / 100));
  const [purchaseDollars, setPurchaseDollars] = useState(String(item.purchase_price / 100));
  const [minStock, setMinStock] = useState(String(item.min_stock));
  const [sqftPerBox, setSqftPerBox] = useState(
    item.sqft_per_box != null ? String(item.sqft_per_box) : '',
  );
  const [sqftPerPiece, setSqftPerPiece] = useState(
    item.sqft_per_piece != null ? String(item.sqft_per_piece) : '',
  );
  const [packQty, setPackQty] = useState(item.pack_qty != null ? String(item.pack_qty) : '');
  const [packUnit, setPackUnit] = useState(item.pack_unit ?? '');
  const [weight, setWeight] = useState(item.weight != null ? String(item.weight) : '');
  const [dimensions, setDimensions] = useState(item.dimensions ?? '');
  const [preferredSupplierId, setPreferredSupplierId] = useState(
    item.preferred_supplier_id != null ? String(item.preferred_supplier_id) : '',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sqftPerBoxError, setSqftPerBoxError] = useState('');

  // Active suppliers for the optional Preferred supplier picker.
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: () => apiFetch<SuppliersResponse>('/api/suppliers?status=active'),
    staleTime: 30_000,
  });
  const suppliers = suppliersData?.data.suppliers ?? [];

  // Photo upload state
  const [imageUrl, setImageUrl] = useState<string | null>(
    item.image_path ? `/api/inventory/${item.id}/image?t=${Date.now()}` : null,
  );
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isGoods = item.category === 'goods';
  const isSqft = isAreaUnit(unit);

  async function handlePhotoUpload(file: File) {
    setPhotoLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/inventory/${item.id}/image`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Upload failed');
      }
      setImageUrl(`/api/inventory/${item.id}/image?t=${Date.now()}`);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setPhotoLoading(false);
    }
  }

  async function handlePhotoDelete() {
    setPhotoLoading(true);
    try {
      const res = await fetch(`/api/inventory/${item.id}/image`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Delete failed');
      }
      setImageUrl(null);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setPhotoLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSqftPerBoxError('');

    if (isSqft && isGoods && (!sqftPerBox.trim() || parseFloat(sqftPerBox) <= 0)) {
      setSqftPerBoxError('Required for Sq.ft. items and must be > 0');
      return;
    }

    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        code: code.trim() || undefined,
        name: name.trim(),
        unit,
        selling_price: Math.round(parseFloat(sellingDollars) * 100),
        purchase_price: Math.round(parseFloat(purchaseDollars) * 100),
        weight: weight.trim() ? parseFloat(weight) : null,
        dimensions: dimensions.trim() || null,
        // Optional preferred supplier — a number when chosen, null to clear.
        preferred_supplier_id: preferredSupplierId ? parseInt(preferredSupplierId, 10) : null,
      };

      if (isGoods) {
        body.min_stock = parseInt(minStock, 10) || 0;

        if (isSqft) {
          body.sqft_per_box = parseFloat(sqftPerBox);
          body.sqft_per_piece = sqftPerPiece.trim() ? parseFloat(sqftPerPiece) : null;
          body.pack_qty = null;
          body.pack_unit = null;
        } else {
          // Omit sqft_per_box rather than sending null: the PATCH endpoint only runs
          // the Sq.ft. box-size validation when the body includes sqft_per_box, so
          // omitting it lets a non-Sq.ft. edit succeed without tripping that rule.
          body.sqft_per_piece = null;
          body.pack_qty = packQty.trim() ? parseFloat(packQty) : null;
          body.pack_unit = packUnit.trim() || null;
        }
      }

      await apiFetch(`/api/inventory/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      toast.success('Item updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-background shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-background z-10">
          <h2 className="text-base font-semibold">Edit Item</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Row: Code + Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ei-code">Code</Label>
              <Input
                id="ei-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if blank"
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ei-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ei-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Category (disabled) + UOM */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ei-category">Category</Label>
              <select id="ei-category" value={item.category} disabled className={SELECT_CLS}>
                <option value="goods">Goods</option>
                <option value="service">Service</option>
              </select>
              <p className="text-xs text-muted-foreground">Cannot be changed after creation.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ei-unit">Unit (UOM)</Label>
              <select
                id="ei-unit"
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value as Unit);
                  setSqftPerBoxError('');
                }}
                disabled={loading}
                className={SELECT_CLS}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Selling + Purchase price — labels follow unit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ei-sell">
                Selling price ($ / {unit}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ei-sell"
                type="number"
                min="0"
                step="0.01"
                value={sellingDollars}
                onChange={(e) => setSellingDollars(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ei-purchase">
                Purchase price ($ / {unit}) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ei-purchase"
                type="number"
                min="0"
                step="0.01"
                value={purchaseDollars}
                onChange={(e) => setPurchaseDollars(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Goods-only fields */}
          {isGoods && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ei-min">Min Stock</Label>
                <Input
                  id="ei-min"
                  type="number"
                  min="0"
                  step="1"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Sq.ft. conditional */}
              {isSqft && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="ei-sqft-box">
                      Sq.ft per box <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="ei-sqft-box"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={sqftPerBox}
                      onChange={(e) => {
                        setSqftPerBox(e.target.value);
                        setSqftPerBoxError('');
                      }}
                      placeholder="e.g. 15.5"
                      disabled={loading}
                    />
                    {sqftPerBoxError && (
                      <p className="text-xs text-destructive">{sqftPerBoxError}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ei-sqft-piece">Sq.ft per piece</Label>
                    <Input
                      id="ei-sqft-piece"
                      type="number"
                      min="0"
                      step="0.01"
                      value={sqftPerPiece}
                      onChange={(e) => setSqftPerPiece(e.target.value)}
                      placeholder="Optional"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* Pcs / Bag: pack contents */}
              {!isSqft && (
                <div className="space-y-1.5">
                  <Label>
                    Pack contents{' '}
                    <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={packQty}
                      onChange={(e) => setPackQty(e.target.value)}
                      placeholder="Qty, e.g. 100"
                      disabled={loading}
                    />
                    <Input
                      value={packUnit}
                      onChange={(e) => setPackUnit(e.target.value)}
                      placeholder="Unit, e.g. Pcs or Lb"
                      disabled={loading}
                      maxLength={20}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shown on estimates as a reference — does not affect price.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Always-available enrichment */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ei-weight">Weight (lb)</Label>
              <Input
                id="ei-weight"
                type="number"
                min="0"
                step="0.01"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="Optional"
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ei-dimensions">Dimensions</Label>
              <Input
                id="ei-dimensions"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
                placeholder="e.g. 24×48 in"
                disabled={loading}
                maxLength={60}
              />
            </div>
          </div>

          {/* Preferred supplier — optional, clearable */}
          <div className="space-y-1.5">
            <Label htmlFor="ei-supplier">
              Preferred supplier{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <select
              id="ei-supplier"
              value={preferredSupplierId}
              onChange={(e) => setPreferredSupplierId(e.target.value)}
              disabled={loading}
              className={SELECT_CLS}
            >
              <option value="">— None —</option>
              {/* Keep the current supplier selectable even if it's now inactive. */}
              {item.preferred_supplier_id != null &&
                !suppliers.some((s) => s.id === item.preferred_supplier_id) && (
                  <option value={String(item.preferred_supplier_id)}>
                    {item.preferred_supplier_name ?? `Supplier #${item.preferred_supplier_id}`}
                  </option>
                )}
              {suppliers.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                  {s.code ? ` (${s.code})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Photo upload */}
          <div className="space-y-1.5">
            <Label>Photo</Label>
            <div className="space-y-2">
              {imageUrl ? (
                <div className="relative w-full max-w-[160px] rounded-md overflow-hidden border">
                  <img src={imageUrl} alt="Item photo" className="w-full h-auto object-cover" />
                  <button
                    type="button"
                    onClick={handlePhotoDelete}
                    disabled={photoLoading}
                    className="absolute top-1 right-1 rounded bg-black/60 p-1 text-white hover:bg-destructive transition-colors disabled:opacity-50"
                    title="Remove photo"
                  >
                    <Trash className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={photoLoading}
                  className="flex items-center gap-2 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground hover:border-primary hover:text-foreground transition-colors disabled:opacity-50"
                >
                  <Upload className="h-4 w-4" />
                  {photoLoading ? 'Uploading…' : 'Upload photo'}
                </button>
              )}
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={photoLoading}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  <Upload className="h-3 w-3" />
                  Replace photo
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handlePhotoUpload(file);
                  e.target.value = '';
                }}
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
